/**
 * THE WEBSITE → CABINET LEG. "User already could be signed in at main web site, they share sign in
 * with authors cabinet web app" (the owner, decision-log row 152).
 *
 * THE MECHANISM IS ALREADY DESIGNED AND NEEDS NO NEW OPERATION. Only the prose around it is
 * phone-first. Three facts, each checked against the generated types:
 *
 *   1. `ClientKind` is `ios | android | web` — `web` is already a seeded value, not something to
 *      add (docs/design/07-api/authors-api.yaml, seeded in the schema at
 *      04-schema.md).
 *   2. `createCabinetHandoffCode` takes `{ client }` and nothing else, and reads the subject from
 *      the verified ID token. A visitor signed in with Firebase on moonx.app has such a token.
 *   3. `/enter?code=…` is the identical landing for all three client kinds.
 *
 * SO THE WHOLE LEG IS: mint with `client: 'web'`, then send the browser to the cabinet's `/enter`.
 * That is what this module does, and it is why the hand-off screen needed no second code path.
 *
 * ── TWO THINGS THAT ARE NOT CODE AND MUST NOT BE ASSUMED AWAY ────────────────────────────────────
 *
 * A. ONE CORS LINE IS MISSING. adr/0002-edge-addressing.md and 02-nfr.md allowlist the
 *    website origin for the PUBLIC routes only, and `/v1/handoff/codes` is an authenticated
 *    authors-api route. Until `moonx.app` is allowlisted for THAT ROUTE, this call fails at the
 *    browser's pre-flight, before any of our code runs. Reported as a finding.
 *
 * B. WHETHER moonx.app CARRIES A FIREBASE SESSION TODAY IS A FACT ABOUT A SHIPPED SITE, and
 *    decision-log row 99 puts how a shipped app renders anything outside this track. It must be
 *    CONFIRMED before this leg is promised to anyone, not assumed. Firebase sessions are
 *    per-origin (adr/0002), so the cabinet cannot see the website's session and cannot check this
 *    for itself.
 *
 * WHERE THIS CODE RUNS. On moonx.app, not in the cabinet — the cabinet is a different origin and
 * has no access to the website's session. It lives here because it is the same two facts as the
 * phone leg, typed once. The cabinet imports only `cabinetEnterUrl`, for the copy on its sign-in
 * screen.
 */
import { createCabinetHandoffCode } from '../api/authors-api';
import type { CallOptions } from '../api/authors-api';
import type { ApiFailure } from '../api/failure';

/** Ruled by adr/0002-edge-addressing.md: the cabinet is this host and the public site is moonx.app. */
export const CABINET_ORIGIN = 'https://authors.moonx.app';
export const WEBSITE_ORIGIN = 'https://moonx.app';

/** Where a minted code is redeemed. The one route the corpus fixes, plus its one parameter. */
export function cabinetEnterUrl(code: string, origin: string = CABINET_ORIGIN): string {
  const url = new URL('/enter', origin);
  url.searchParams.set('code', code);
  return url.toString();
}

export type WebHandoffResult =
  | { readonly ok: true; readonly enterUrl: string; readonly expiresInSeconds: number }
  | { readonly ok: false; readonly failure: ApiFailure };

/**
 * Mints a hand-off code for a visitor already signed in on the website and returns the cabinet URL
 * to send them to.
 *
 * `idToken` is the WEBSITE's Firebase ID token — the caller has it, this module never asks Firebase
 * for one, because on the website's origin the cabinet's SDK does not exist.
 *
 * THE CODE LIVES 120 SECONDS ("120 at v1, and a config value rather than a constant"), so the
 * returned URL must be followed immediately. It is deliberately not stored, not logged and not put
 * anywhere a second reader could find it: this response "is the only time it exists in readable
 * form".
 */
export async function startWebHandoff(
  idToken: string,
  options: CallOptions = {},
): Promise<WebHandoffResult> {
  const result = await createCabinetHandoffCode('web', idToken, options);
  if (!result.ok) {
    return { ok: false, failure: result.failure };
  }
  return {
    ok: true,
    enterUrl: cabinetEnterUrl(result.value.code),
    expiresInSeconds: result.value.expiresInSeconds,
  };
}
